import { ColumnDef, Row, createColumnHelper } from '@tanstack/react-table';
import { Checkbox } from 'ui';

import { RowData } from '../../../src/utils';

const ROW_SELECTION_COLUMN_ID = '__select';

/**
 *
 * Creates a row selection column that is by default not printable
 *
 * This can be over ridden when instantiating the column and using the meta property on the args
 */
function createRowSelectionColumn<TData extends RowData>() {
  const columnHelper = createColumnHelper<TData>();
  type Accessor = typeof columnHelper.accessor;
  type TableMeta = Parameters<Accessor>[1]['meta'];
  type ColumnArgs = {
    meta?: TableMeta;
    /**
     * Determines if the checkbox should be visually checked regardless of the row selection state
     * This is useful for when the row selection is disabled but the checkbox should still be checked
     * or the visually representation would be useful to the user
     */
    isRowVisuallyChecked?: (row: Row<TData>) => boolean;
  };

  const selectionColumn = ({ meta, isRowVisuallyChecked }: ColumnArgs = {}) =>
    columnHelper.display({
      size: 45,
      id: ROW_SELECTION_COLUMN_ID,
      header: ({ table }) => (
        <Checkbox
          size="sm"
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          size="sm"
          checked={Boolean(row.getIsSelected() || isRowVisuallyChecked?.(row))}
          disabled={!row.getCanSelect()}
          indeterminate={row.getIsSomeSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      meta: {
        isPrintable: false,
        ...meta,
      },
    });

  // Allow the column id '__select' to be used and still acceptable by the table data
  return selectionColumn as unknown as (args?: ColumnArgs) => ColumnDef<TData>;
}

export { createRowSelectionColumn };
